import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { TargetIcon, CompassIcon, HandshakeIcon, WrenchIcon, HeartHandshakeIcon, CoinsIcon, ZapIcon } from 'lucide-react';
import { Badge } from '../../Badge';
import { Card, CardHeader, CardBody } from '../../Card';
const PILLARS: {
  icon: React.ReactNode;
  title: string;
  text: string;
}[] = [{
  icon: <TargetIcon className="h-5 w-5" aria-hidden="true" data-id="element-93" />,
  title: 'ミッション',
  text: 'IT人材不足や業務の非効率という課題を、費用対効果の高いデジタルの力で解決し、経営者が本業に集中できる環境を整えます。'
}, {
  icon: <CompassIcon className="h-5 w-5" aria-hidden="true" data-id="element-94" />,
  title: 'ビジョン',
  text: '横須賀の中小企業にとって、最も信頼される不可欠なデジタル変革のパートナーになること。'
}, {
  icon: <HandshakeIcon className="h-5 w-5" aria-hidden="true" data-id="element-95" />,
  title: 'お約束',
  text: 'ITの知識も大きな初期投資もなくても、日々の悩みをすぐに解決し、本来の事業に集中できる環境を、信頼できるパートナーとして一緒に創ります。'
}];
const VALUES: {
  icon: React.ReactNode;
  title: string;
  text: string;
  accent: string;
}[] = [{
  icon: <WrenchIcon className="h-5 w-5" aria-hidden="true" data-id="element-96" />,
  title: '根本的な課題解決',
  text: '表面的な対処ではなく、困りごとの本当の原因から解決します。',
  accent: 'bg-deep-navy text-white'
}, {
  icon: <HeartHandshakeIcon className="h-5 w-5" aria-hidden="true" data-id="element-97" />,
  title: 'ハンズオンの支援と指導',
  text: '導入して終わりにせず、現場で使いこなせるまで並走します。',
  accent: 'bg-sky-blue/15 text-deep-navy'
}, {
  icon: <CoinsIcon className="h-5 w-5" aria-hidden="true" data-id="element-98" />,
  title: '費用対効果と高信頼性',
  text: '無駄を省き、投資に見合う成果と安心して任せられる品質を。',
  accent: 'bg-sage-green/15 text-sage-green'
}, {
  icon: <ZapIcon className="h-5 w-5" aria-hidden="true" data-id="element-99" />,
  title: '迅速な導入と効果実証',
  text: '最短翌日から。早く始めて、具体的な効果を一緒に確かめます。',
  accent: 'bg-amber/15 text-amber'
}];
export function MissionValues() {
  const reduce = useReducedMotion();
  return <section id="mission" className="w-full bg-surface-muted" data-id="element-100">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24" data-id="element-101">
        {/* Mission / Vision / Promise */}
        <div className="text-center" data-id="element-102">
          <div className="flex justify-center" data-id="element-103">
            <Badge variant="navy" style={{
            color: '#0F4C81',
            backgroundColor: 'rgba(15, 76, 129, 0.1)',
            borderColor: 'rgba(15, 76, 129, 0.2)'
          }} className="border" data-id="element-104">
              私たちの軸
            </Badge>
          </div>
          <h2 className="mt-4 font-heading text-2xl font-bold text-text sm:text-3xl" data-id="element-105">
            なぜ、伴走にこだわるのか
          </h2>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3" data-id="element-106">
          {PILLARS.map((p, i) => <motion.div key={p.title} initial={reduce ? undefined : {
          opacity: 0,
          y: 18
        }} whileInView={reduce ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: i * 0.08
        }} data-id="element-107">
              <Card variant="elevated" className="h-full" data-id="element-108">
                <CardHeader data-id="element-109">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-deep-navy text-white" data-id="element-110">
                    {p.icon}
                  </span>
                  <h3 className="mt-3 font-heading text-lg font-bold text-text" data-id="element-111">
                    {p.title}
                  </h3>
                </CardHeader>
                <CardBody data-id="element-112">{p.text}</CardBody>
              </Card>
            </motion.div>)}
        </div>

        {/* Values */}
        <div className="mt-16 text-center" data-id="element-113">
          <h3 className="font-heading text-xl font-bold text-text sm:text-2xl" data-id="element-114">
            大切にしている4つの価値観
          </h3>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-115">
          {VALUES.map((v, i) => <motion.div key={v.title} initial={reduce ? undefined : {
          opacity: 0,
          y: 18
        }} whileInView={reduce ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: i * 0.06
        }} data-id="element-116">
              <Card variant="outlined" className="h-full" data-id="element-117">
                <CardHeader data-id="element-118">
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-lg ${v.accent}`} data-id="element-119">
                    {v.icon}
                  </span>
                  <h4 className="mt-3 font-heading text-base font-bold text-text" data-id="element-120">
                    {v.title}
                  </h4>
                </CardHeader>
                <CardBody data-id="element-121">{v.text}</CardBody>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}